import { z } from "zod";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { cn } from "@/ui/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/components/ui/card";
import { Button } from "@/ui/components/ui/button";
import { Loader2 } from "lucide-react";
import TextInputController from "@/shared/components/ui/form/input-with-controller";

const AcceptInviteSchema = z
  .object({
    password: z
      .string({ required_error: "validation.required" })
      .min(1, { message: "validation.required" }),
    confirmPassword: z
      .string({ required_error: "validation.required" })
      .min(1, { message: "validation.required" }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "validation.passwordsDontMatch",
  });

export type AcceptInviteInput = z.infer<typeof AcceptInviteSchema>;

interface AcceptInviteFormProps {
  onSubmit: (data: AcceptInviteInput) => void;
  isLoading: boolean;
}

const AcceptInviteForm = ({ onSubmit, isLoading }: AcceptInviteFormProps) => {
  const { t } = useTranslation("auth");
  const form = useForm<AcceptInviteInput>({
    resolver: zodResolver(AcceptInviteSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const handleSubmit = async (data: AcceptInviteInput) => {
    onSubmit(data);
  };

  return (
    <div className={cn("flex flex-col gap-6 max-w-96 mx-auto")}>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t("acceptInvite.title")}</CardTitle>
          <CardDescription>
            {t("acceptInvite.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="grid gap-6">
                <div className="grid gap-4">
                  <TextInputController
                    type="password"
                    placeholder={t("acceptInvite.form.passwordPlaceholder")}
                    title={t("acceptInvite.form.passwordLabel")}
                    name="password"
                  />
                  <TextInputController
                    type="password"
                    placeholder={t("acceptInvite.form.confirmPlaceholder")}
                    title={t("acceptInvite.form.confirmLabel")}
                    name="confirmPassword"
                  />
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="animate-spin mr-2" />}
                    {t("acceptInvite.form.submit")}
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInviteForm;